// src/domains/onboarding/pages/ClinicSignupPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, establishmentsService } from 'infrastructure/firebase';
import { isValidEmail, isValidCNPJ, formatCNPJ, isValidPhone } from 'shared/utils/security';
import { ESTABLISHMENT_CATEGORIES } from 'shared/config/plans';
import { ROLE } from 'shared/config/roles';

const T = {
  brand: '#6B5876', accent: '#C9929A', border: '#E8E5EC',
  s2: '#FAF7FB', s3: '#F0EDF3', text: '#3D3447', text2: '#6B6070', muted: '#A8A1B0',
  sans: "'DM Sans',sans-serif", serif: "'Instrument Serif',serif",
};

const inputStyle = {
  width: '100%', padding: '11px 14px', border: `1.5px solid ${T.border}`,
  borderRadius: 12, fontSize: 14, fontFamily: T.sans, outline: 'none',
  boxSizing: 'border-box', color: T.text, background: '#fff',
};

const btnPrimary = (disabled) => ({
  width: '100%', padding: 14, background: disabled ? T.muted : T.brand,
  color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: T.sans,
  opacity: disabled ? 0.7 : 1,
});

export default function ClinicSignupPage() {
  const navigate = useNavigate();
  const [step, setStep]     = useState(1); // 1=dados, 2=senha, 3=sucesso
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  // Dados da clínica
  const [razaoSocial, setRazaoSocial]   = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpj, setCnpj]                 = useState('');
  const [email, setEmail]               = useState('');
  const [telefone, setTelefone]         = useState('');
  const [categoria, setCategoria]       = useState('');

  // Dados de acesso
  const [senha, setSenha]     = useState('');
  const [senha2, setSenha2]   = useState('');
  const [adminName, setAdminName] = useState('');

  const isMobile = window.innerWidth < 768;

  const validateStep1 = () => {
    if (!razaoSocial.trim() || razaoSocial.trim().length < 2) return 'Informe a Razão Social.';
    if (!nomeFantasia.trim())                                  return 'Informe o Nome Fantasia.';
    if (!isValidCNPJ(cnpj))                                   return 'CNPJ inválido. Verifique os dígitos.';
    if (!isValidEmail(email))                                  return 'E-mail inválido.';
    if (telefone && !isValidPhone(telefone))                   return 'Telefone inválido.';
    if (!categoria)                                            return 'Selecione a categoria do estabelecimento.';
    return null;
  };

  const handleStep1 = (e) => {
    e.preventDefault();
    setError('');
    const err = validateStep1();
    if (err) { setError(err); return; }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!adminName.trim() || adminName.trim().length < 2) { setError('Informe seu nome completo.'); return; }
    if (senha.length < 6)   { setError('A senha deve ter no mínimo 6 caracteres.'); return; }
    if (senha !== senha2)   { setError('As senhas não coincidem.'); return; }

    setLoading(true);
    const emailNorm = email.trim().toLowerCase();
    try {
      // SEGURANÇA: pré-gera o ID do estabelecimento e registra o admin JÁ com
      // estabelecimentoId vinculado. Assim o documento do usuário NUNCA existe no
      // estado "admin sem estabelecimento" (que era interpretado como super admin).
      const estabId = establishmentsService.newId();

      // 1. Cria o usuário admin já vinculado ao estabelecimento (via app secundário,
      //    sem alterar a sessão atual e sem criar sessão de super admin).
      const cred = await authService.register({
        name:              adminName.trim(),
        email:             emailNorm,
        password:          senha,
        role:              ROLE.CLINIC_ADMIN,
        phone:             telefone.trim(),
        specialty:         '',
        estabelecimentoId: estabId,   // vínculo desde a criação
      });

      // 2. Autentica na app principal (agora como admin de clínica legítimo, NÃO super admin)
      await authService.login(emailNorm, senha);

      // 3. Cria o documento do estabelecimento com o ID pré-gerado.
      //    Se falhar, faz rollback: deleta o usuário recém-criado e desloga.
      try {
        await establishmentsService.addWithId(estabId, {
          razaoSocial:  razaoSocial.trim(),
          nomeFantasia: nomeFantasia.trim(),
          cnpj:         cnpj.replace(/\D/g, ''),
          email:        emailNorm,
          telefone:     telefone.trim(),
          categoria,
          planoId:      null,
          foto:         null,
          banner:       null,
          descricao:    '',
          adminUid:     cred.uid,
        });
      } catch (estabErr) {
        // Rollback: remove o usuário órfão e encerra a sessão para não deixar acesso indevido
        await authService.deleteCurrentUser().catch(() => {});
        await authService.logout().catch(() => {});
        throw estabErr;
      }

      setStep(3);
    } catch (err) {
      // Garante que nenhuma sessão residual permaneça após qualquer falha
      if (err?.code === 'auth/email-already-in-use') {
        await authService.logout().catch(() => {});
        setError('Este e-mail já possui cadastro. Faça login.');
      } else if (err?.code === 'auth/weak-password') {
        setError('Senha muito fraca.');
      } else {
        await authService.logout().catch(() => {});
        setError('Erro ao criar conta. Tente novamente.');
        console.error('[ClinicSignup]', err?.code || err?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.s2, fontFamily: T.sans,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontFamily: T.serif, fontSize: 32, color: T.brand }}>
            Agenda<span style={{ color: '#B5A2D2' }}>Pro</span>
          </div>
          <p style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
            Cadastro de Clínica / Estabelecimento
          </p>
        </div>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 20 : 32,
          boxShadow: '0 4px 20px rgba(107,88,118,0.08)' }}>

          {/* Etapas */}
          {step < 3 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              {['Dados da clínica', 'Dados de acesso'].map((label, i) => (
                <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ height: 4, borderRadius: 4, marginBottom: 6,
                    background: i + 1 <= step ? T.brand : T.border }} />
                  <span style={{ fontSize: 11, color: i + 1 <= step ? T.brand : T.muted,
                    fontWeight: i + 1 === step ? 600 : 400 }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Sucesso */}
          {step === 3 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
              <div style={{ fontFamily: T.serif, fontSize: 24, color: T.text, marginBottom: 8 }}>
                Clínica cadastrada!
              </div>
              <p style={{ color: T.text2, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                Conta criada com sucesso. Agora escolha seu plano para começar a usar o AgendaPro.
              </p>
              <button onClick={() => navigate('/signup/plan', { state: { estabelecimentoId: undefined } })}
                style={btnPrimary(false)}>
                Escolher plano →
              </button>
              <div style={{ marginTop: 12 }}>
                <button onClick={() => navigate('/login')}
                  style={{ background: 'none', border: 'none', color: T.muted, fontSize: 13,
                    cursor: 'pointer', fontFamily: T.sans }}>
                  Fazer isso depois — entrar no sistema
                </button>
              </div>
            </div>

          /* Passo 1 — Dados da clínica */
          ) : step === 1 ? (
            <>
              <div style={{ fontFamily: T.serif, fontSize: 22, color: T.text, marginBottom: 4 }}>
                Dados da clínica
              </div>
              <p style={{ color: T.muted, fontSize: 13, marginBottom: 20 }}>
                Pessoa Jurídica — todos os campos com * são obrigatórios.
              </p>

              {error && (
                <div style={{ padding: '10px 14px', background: '#F0DADD', borderRadius: 10,
                  color: '#7A3C44', fontSize: 13, marginBottom: 16 }}>{error}</div>
              )}

              <form onSubmit={handleStep1}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Razão Social *
                  </label>
                  <input value={razaoSocial} onChange={e => setRazaoSocial(e.target.value)}
                    required placeholder="Razão Social Ltda." style={inputStyle} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Nome Fantasia *
                  </label>
                  <input value={nomeFantasia} onChange={e => setNomeFantasia(e.target.value)}
                    required placeholder="Nome exibido para clientes" style={inputStyle} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                      CNPJ *
                    </label>
                    <input value={cnpj} onChange={e => setCnpj(formatCNPJ(e.target.value))}
                      placeholder="00.000.000/0000-00" maxLength={18}
                      style={{ ...inputStyle,
                        borderColor: cnpj && cnpj.replace(/\D/g,'').length === 14 && !isValidCNPJ(cnpj)
                          ? T.accent : T.border }} />
                    {cnpj && cnpj.replace(/\D/g,'').length === 14 && !isValidCNPJ(cnpj) && (
                      <span style={{ fontSize: 11, color: T.accent, marginTop: 3, display: 'block' }}>
                        CNPJ inválido
                      </span>
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                      Telefone
                    </label>
                    <input type="tel" value={telefone} onChange={e => setTelefone(e.target.value)}
                      placeholder="(11) 99999-9999" style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    E-mail *
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required placeholder="contato@suaclinica.com.br" style={inputStyle} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 8 }}>
                    Categoria *
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {ESTABLISHMENT_CATEGORIES.map(cat => (
                      <button key={cat.id} type="button"
                        onClick={() => setCategoria(cat.id)}
                        style={{ padding: '10px 12px', border: `1.5px solid ${categoria === cat.id ? T.brand : T.border}`,
                          borderRadius: 10, cursor: 'pointer', background: categoria === cat.id ? T.s3 : '#fff',
                          display: 'flex', alignItems: 'center', gap: 8, fontFamily: T.sans,
                          color: categoria === cat.id ? T.brand : T.text2, fontSize: 13,
                          transition: 'all 0.15s' }}>
                        <span style={{ fontSize: 18 }}>{cat.icon}</span>
                        <span style={{ fontWeight: categoria === cat.id ? 600 : 400 }}>{cat.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <button type="submit" style={btnPrimary(false)}>Continuar →</button>
              </form>
            </>

          /* Passo 2 — Dados de acesso */
          ) : (
            <>
              <button onClick={() => { setStep(1); setError(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted,
                  fontSize: 13, fontFamily: T.sans, padding: 0, marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 6 }}>
                ← Voltar
              </button>
              <div style={{ fontFamily: T.serif, fontSize: 22, color: T.text, marginBottom: 4 }}>
                Dados de acesso
              </div>
              <p style={{ color: T.muted, fontSize: 13, marginBottom: 20 }}>
                Crie a senha do administrador da clínica.
              </p>

              {error && (
                <div style={{ padding: '10px 14px', background: '#F0DADD', borderRadius: 10,
                  color: '#7A3C44', fontSize: 13, marginBottom: 16 }}>{error}</div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Seu nome completo *
                  </label>
                  <input value={adminName} onChange={e => setAdminName(e.target.value)}
                    required placeholder="Nome do responsável" style={inputStyle} />
                </div>
                <div style={{ marginBottom: 4, background: T.s2, borderRadius: 10, padding: '8px 12px',
                  fontSize: 13, color: T.text2 }}>
                  Entrando com: <strong>{email}</strong>
                </div>
                <div style={{ marginBottom: 12, marginTop: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Senha * <span style={{ fontSize: 11, color: T.muted, fontWeight: 400 }}>(mín. 6 caracteres)</span>
                  </label>
                  <input type="password" value={senha} onChange={e => setSenha(e.target.value)}
                    required placeholder="••••••••" autoComplete="new-password" style={inputStyle} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Confirmar senha *
                  </label>
                  <input type="password" value={senha2} onChange={e => setSenha2(e.target.value)}
                    required placeholder="••••••••" autoComplete="new-password"
                    style={{ ...inputStyle,
                      borderColor: senha2 && senha !== senha2 ? T.accent : T.border }} />
                  {senha2 && senha !== senha2 && (
                    <span style={{ fontSize: 11, color: T.accent, marginTop: 3, display: 'block' }}>
                      As senhas não coincidem
                    </span>
                  )}
                </div>
                <button type="submit" disabled={loading} style={btnPrimary(loading)}>
                  {loading ? 'Criando conta...' : 'Criar conta da clínica →'}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Link para login */}
        {step < 3 && (
          <p style={{ textAlign: 'center', fontSize: 13, color: T.muted, marginTop: 16 }}>
            Já tem conta?{' '}
            <button onClick={() => navigate('/login')}
              style={{ background: 'none', border: 'none', color: T.brand, fontWeight: 600,
                cursor: 'pointer', fontFamily: T.sans, fontSize: 13 }}>
              Entrar
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
